/**
 * BUNKER 84 — DDA raycaster.
 *
 * One ray per screen column. For each ray we step through grid cells
 * using the classic Lode/Permadi DDA: at each step we advance to
 * whichever next cell boundary (vertical or horizontal) is closer,
 * and stop the moment we land in a solid tile.
 *
 * Returns the perpendicular wall distance (NOT the raw ray length —
 * that would produce a fisheye lens curve), the wall tile id, and
 * whether the ray hit a vertical or horizontal grid line. The renderer
 * uses the wall id to pick a base color, and the side to apply NS/EW
 * shading so the player gets a "lit corridor" depth cue.
 *
 * **Hot loop.** This is called 480 times per frame at 480×270.
 * No allocations, no Math.atan2, no Math.sqrt — every operation here
 * shows up 60 × 480 = 28,800 times per second.
 */

import { isSolid, tileAt } from './tiles.js';

export interface CastResult {
  /** Perpendicular distance from the camera plane to the wall hit. */
  distance: number;
  /** Tile id of the wall the ray hit (1..8 per tiles.ts). */
  wallType: number;
  /**
   * Which axis the ray crossed when it hit. `'x'` = vertical grid line
   * (EW-facing wall surface); `'y'` = horizontal grid line (NS-facing).
   * The renderer darkens one side relative to the other for fake lighting.
   */
  side: 'x' | 'y';
}

/**
 * The result object is reused across calls in the hot loop to avoid
 * 480 allocations per frame. Callers must consume the fields before
 * the next call.
 */
const RESULT: CastResult = { distance: 0, wallType: 1, side: 'x' };

/**
 * Cast one ray for screen column `col` of `screenWidth`, using a
 * symmetric camera plane perpendicular to the player's facing vector.
 *
 * The camera plane length controls FOV: planeLength = tan(fov/2). With
 * fov = π/3 (60°) the plane length is ~0.577, which is what most
 * Wolf3D-style engines use.
 *
 * @param playerX     player X in tile-space (floats)
 * @param playerY     player Y in tile-space
 * @param dirX        unit-length facing direction X (cos(angle))
 * @param dirY        unit-length facing direction Y (sin(angle))
 * @param planeX      camera plane vector X (perpendicular to dir, length tan(fov/2))
 * @param planeY      camera plane vector Y
 * @param map         level tile data
 * @param col         screen column index (0..screenWidth-1)
 * @param screenWidth total screen columns
 * @returns           reused {@link CastResult}; consume before the next call
 */
export function castColumn(
  playerX: number,
  playerY: number,
  dirX: number,
  dirY: number,
  planeX: number,
  planeY: number,
  map: Uint8Array,
  col: number,
  screenWidth: number
): CastResult {
  // Map column index to camera space x in [-1, 1].
  const cameraX = (2 * col) / screenWidth - 1;

  // Ray direction = facing + cameraX * planeVector.
  const rayDirX = dirX + planeX * cameraX;
  const rayDirY = dirY + planeY * cameraX;

  // Current tile coords.
  let mapX = Math.floor(playerX);
  let mapY = Math.floor(playerY);

  // Length of ray from one X or Y side to the next X or Y side.
  // Using the optimization |1 / rayDir| — avoids the sqrt the naive
  // form would need. Guard against rayDir = 0 with a huge fallback;
  // the multiply downstream will just produce a number larger than
  // any wall distance, so the other axis dominates.
  const deltaDistX = rayDirX === 0 ? 1e30 : Math.abs(1 / rayDirX);
  const deltaDistY = rayDirY === 0 ? 1e30 : Math.abs(1 / rayDirY);

  // Step direction (+1 / -1) per axis + sideDist to the first grid line.
  let stepX: number;
  let sideDistX: number;
  if (rayDirX < 0) {
    stepX = -1;
    sideDistX = (playerX - mapX) * deltaDistX;
  } else {
    stepX = 1;
    sideDistX = (mapX + 1.0 - playerX) * deltaDistX;
  }

  let stepY: number;
  let sideDistY: number;
  if (rayDirY < 0) {
    stepY = -1;
    sideDistY = (playerY - mapY) * deltaDistY;
  } else {
    stepY = 1;
    sideDistY = (mapY + 1.0 - playerY) * deltaDistY;
  }

  // DDA: step until we land in a solid tile.
  // 'x' = last step was along the X axis (we crossed a vertical grid line).
  // 'y' = last step was along the Y axis.
  let side: 'x' | 'y' = 'x';
  // Safety bound — the level is 32x32; 96 steps is way more than any
  // ray could need. Without this, a malformed map or a ray that
  // somehow escapes the outer wall ring would lock the frame.
  let hitTile = 0;
  for (let i = 0; i < 96; i++) {
    if (sideDistX < sideDistY) {
      sideDistX += deltaDistX;
      mapX += stepX;
      side = 'x';
    } else {
      sideDistY += deltaDistY;
      mapY += stepY;
      side = 'y';
    }
    const t = tileAt(map, mapX, mapY);
    if (isSolid(t)) {
      hitTile = t;
      break;
    }
  }

  // Perpendicular distance — subtract one deltaDist because the
  // sideDist we accumulated is the distance to the FAR side of the
  // tile we just stepped into; we want the distance to the NEAR
  // boundary, i.e. the wall surface.
  let perpDist: number;
  if (side === 'x') {
    perpDist = sideDistX - deltaDistX;
  } else {
    perpDist = sideDistY - deltaDistY;
  }
  // Guard against ~0 distances (player inside a wall) — clamp to a
  // small positive so the wall height calc doesn't divide by zero.
  if (perpDist < 0.0001) perpDist = 0.0001;

  RESULT.distance = perpDist;
  RESULT.wallType = hitTile || 1;
  RESULT.side = side;
  return RESULT;
}
